import type {
  AssetRecord,
  DesignObject,
  EditorState,
  ID,
  ProductConfig,
  ProductViewId,
  SurfacePrintEffectKind,
  SurfacePrintEffectSetting,
} from '../domain/types';
import { createId } from '../domain/types';
import {
  createDefaultSurfacePrintEffects,
  resolveSurfacePrintEffects,
} from '../domain/print-effects';
import { resolveGarmentSelection, resolvePrintableArea, resolveProductColour } from '../products';
import { objectBounds } from '../validation/geometry';

export interface Command<TState> {
  readonly label: string;
  execute(state: TState): TState;
  undo(state: TState): TState;
}

export class MutationCommand implements Command<EditorState> {
  readonly label: string;
  private readonly before: EditorState;
  private readonly after: EditorState;

  constructor(
    label: string,
    before: EditorState,
    mutate: (draft: EditorState) => void,
    preserveAcknowledgedWarnings = false,
  ) {
    this.label = label;
    this.before = structuredClone(before);
    this.after = structuredClone(before);
    mutate(this.after);
    if (!preserveAcknowledgedWarnings) this.after.design.acknowledgedWarnings = [];
    this.after.design.updatedAt = new Date().toISOString();
  }

  execute(): EditorState {
    return structuredClone(this.after);
  }

  undo(): EditorState {
    return structuredClone(this.before);
  }
}

function activeObjects(state: EditorState): DesignObject[] {
  const surface = state.design.surfaces[state.activeViewId];
  if (!surface) throw new Error(`Missing surface ${state.activeViewId}`);
  return surface.objects;
}

export const commands = {
  addObject(state: EditorState, object: DesignObject, asset?: AssetRecord): MutationCommand {
    return new MutationCommand(`Pridať ${object.name}`, state, draft => {
      activeObjects(draft).push(structuredClone(object));
      if (asset) draft.design.assets[asset.id] = structuredClone(asset);
      draft.selectedObjectIds = [object.id];
    });
  },

  updateObject(
    state: EditorState,
    id: ID,
    patch: Partial<DesignObject>,
    label = 'Upraviť objekt',
  ): MutationCommand {
    return new MutationCommand(label, state, draft => {
      const object = activeObjects(draft).find(item => item.id === id);
      if (!object) return;
      Object.assign(object, structuredClone(patch), { updatedAt: new Date().toISOString() });
    });
  },

  updateSelected(
    state: EditorState,
    mutate: (object: DesignObject) => void,
    label = 'Upraviť výber',
  ): MutationCommand {
    return new MutationCommand(label, state, draft => {
      const ids = new Set(draft.selectedObjectIds);
      activeObjects(draft)
        .filter(object => ids.has(object.id))
        .forEach(object => {
          mutate(object);
          object.updatedAt = new Date().toISOString();
        });
    });
  },

  deleteSelected(state: EditorState): MutationCommand {
    return new MutationCommand('Odstrániť objekty', state, draft => {
      const ids = new Set(draft.selectedObjectIds);
      const surface = draft.design.surfaces[draft.activeViewId];
      if (surface) surface.objects = surface.objects.filter(object => !ids.has(object.id));
      draft.selectedObjectIds = [];
    });
  },

  duplicateSelected(state: EditorState): MutationCommand {
    return new MutationCommand('Duplikovať objekty', state, draft => {
      const objects = activeObjects(draft);
      const selected = new Set(draft.selectedObjectIds);
      const clones = objects
        .filter(object => selected.has(object.id))
        .map(object => ({
          ...structuredClone(object),
          id: createId(object.type),
          name: `${object.name} – kópia`,
          transform: {
            ...structuredClone(object.transform),
            positionMm: {
              x: object.transform.positionMm.x + 5,
              y: object.transform.positionMm.y + 5,
            },
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));
      objects.push(...clones);
      draft.selectedObjectIds = clones.map(object => object.id);
    });
  },

  reorder(state: EditorState, id: ID, direction: 'up' | 'down'): MutationCommand {
    return new MutationCommand('Zmeniť poradie vrstvy', state, draft => {
      const objects = activeObjects(draft);
      const index = objects.findIndex(object => object.id === id);
      const next = direction === 'up' ? index + 1 : index - 1;
      if (index < 0 || next < 0 || next >= objects.length) return;
      [objects[index], objects[next]] = [objects[next]!, objects[index]!];
    });
  },

  align(
    state: EditorState,
    alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom',
  ): MutationCommand {
    return new MutationCommand(`Zarovnať ${alignment}`, state, draft => {
      const surface = draft.design.surfaces[draft.activeViewId];
      if (!surface) return;
      const selected = surface.objects.filter(object =>
        draft.selectedObjectIds.includes(object.id),
      );
      selected.forEach(object => {
        const bounds = objectBounds(object);
        const { positionMm } = object.transform;
        if (alignment === 'left') positionMm.x -= bounds.x;
        if (alignment === 'center') {
          positionMm.x += (surface.printAreaMm.width - bounds.width) / 2 - bounds.x;
        }
        if (alignment === 'right') {
          positionMm.x += surface.printAreaMm.width - bounds.width - bounds.x;
        }
        if (alignment === 'top') positionMm.y -= bounds.y;
        if (alignment === 'middle') {
          positionMm.y += (surface.printAreaMm.height - bounds.height) / 2 - bounds.y;
        }
        if (alignment === 'bottom') {
          positionMm.y += surface.printAreaMm.height - bounds.height - bounds.y;
        }
      });
    });
  },

  updateSurfacePrintEffect(
    state: EditorState,
    kind: SurfacePrintEffectKind,
    patch: Partial<SurfacePrintEffectSetting>,
    label = 'Upraviť efekt potlače',
  ): MutationCommand {
    return new MutationCommand(label, state, draft => {
      const surface = draft.design.surfaces[draft.activeViewId];
      if (!surface) return;
      const current = resolveSurfacePrintEffects(surface.printEffects);
      surface.printEffects = resolveSurfacePrintEffects({
        ...current,
        [kind]: { ...current[kind], ...structuredClone(patch) },
      });
    });
  },

  resetSurfacePrintEffects(state: EditorState): MutationCommand {
    return new MutationCommand('Resetovať efekty potlače', state, draft => {
      const surface = draft.design.surfaces[draft.activeViewId];
      if (surface) surface.printEffects = createDefaultSurfacePrintEffects();
    });
  },

  changeProductOption(
    state: EditorState,
    patch: Partial<Pick<EditorState['design'], 'colourId' | 'technology'>>,
  ): MutationCommand {
    return new MutationCommand('Zmeniť parameter produktu', state, draft =>
      Object.assign(draft.design, patch),
    );
  },

  changeQuantity(state: EditorState, quantity: number): MutationCommand {
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      throw new RangeError('Quantity must be a positive whole number');
    }
    return new MutationCommand(
      'Zmeniť počet tričiek',
      state,
      draft => {
        draft.design.quantity = quantity;
      },
      true,
    );
  },

  changeGarmentSize(
    state: EditorState,
    product: ProductConfig,
    variantId: string,
    size: string,
  ): MutationCommand {
    // Resolve eagerly so an invalid variant/size never enters command history.
    const selection = resolveGarmentSelection(product, variantId, size);
    const nextColour = resolveProductColour(product, state.design.colourId, selection.variant.id);
    const resolvedAreas = new Map(
      product.printableAreas.map(area => [
        area.viewId,
        resolvePrintableArea(product, variantId, size, area.viewId),
      ]),
    );

    return new MutationCommand('Zmeniť veľkosť trička', state, draft => {
      draft.design.variantId = selection.variant.id;
      draft.design.size = size;
      draft.design.colourId = nextColour.id;
      draft.design.acknowledgedWarnings = [];

      resolvedAreas.forEach((nextArea, viewId) => {
        const surface = draft.design.surfaces[viewId];
        if (!surface) return;
        const horizontalOffsetMm = (nextArea.sizeMm.width - surface.printAreaMm.width) / 2;
        surface.objects.forEach(object => {
          object.transform.positionMm.x += horizontalOffsetMm;
        });
        surface.printAreaMm = structuredClone(nextArea.sizeMm);
      });
    });
  },

  acknowledgeWarning(state: EditorState, warningId: string, acknowledged = true): MutationCommand {
    return new MutationCommand(
      acknowledged ? 'Potvrdiť upozornenie' : 'Zrušiť potvrdenie',
      state,
      draft => {
        const warnings = new Set(draft.design.acknowledgedWarnings);
        if (acknowledged) warnings.add(warningId);
        else warnings.delete(warningId);
        draft.design.acknowledgedWarnings = [...warnings].sort();
      },
      true,
    );
  },

  replaceImageVersion(
    state: EditorState,
    sourceObjectId: ID,
    object: DesignObject,
    asset: AssetRecord,
  ): MutationCommand {
    return new MutationCommand('Pridať AI verziu', state, draft => {
      const objects = activeObjects(draft);
      const sourceIndex = objects.findIndex(item => item.id === sourceObjectId);
      objects.splice(sourceIndex + 1, 0, object);
      draft.design.assets[asset.id] = asset;
      draft.selectedObjectIds = [object.id];
    });
  },
};

export function commandForSurface(
  state: EditorState,
  viewId: ProductViewId,
  label: string,
  mutate: (objects: DesignObject[]) => void,
): MutationCommand {
  return new MutationCommand(label, state, draft => {
    const surface = draft.design.surfaces[viewId];
    if (surface) mutate(surface.objects);
  });
}
